import { ReactElement, useCallback, useEffect, useState } from 'react';
import { Notification } from '../../../interfaces/backend/Notification';
import Paragraph from 'antd/es/typography/Paragraph';
import { useParams } from 'react-router-dom';
import { Channels } from '../../common/channelsEnum';
import { Template } from '../../../interfaces/backend/Template';
import { api } from '../../../utils/axiosConfig';
import { getAllTags } from '../LiquidizedPreview';
import { Alert, Button, Col, Divider, Row, Tooltip, Typography } from 'antd';
import { CopyOutlined, LoadingOutlined } from '@ant-design/icons';
import { darcula } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import SyntaxHighlighter from 'react-syntax-highlighter/dist/esm/default-highlight';

interface Props {
  notification: Notification;
}

const Parameters = (props: Props): ReactElement => {
  const { notificationId, accountId } =
    useParams<{ notificationId: string; accountId: string }>();
  const [templates, setTemplates] = useState<Template[]>();
  const [blockContent, setBlockContent] = useState<string>('');

  const fetchTemplates = useCallback(async (): Promise<void> => {
    const responses = await Promise.all(
      Object.values(Channels).map((channel) => {
        const url = `/${accountId}/notifications/${notificationId}/templates/${channel}`;
        return api('GET', url);
      })
    );

    const newTemplates = responses.map(
      (res) => (res as Record<'data', Template[]>).data
    );

    setTemplates(newTemplates.flat());
  }, [accountId, notificationId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates, accountId]);

  useEffect(() => {
    if (!templates) return;

    setBlockContent(
      JSON.stringify(
        getAllTags(
          templates.filter((t) =>
            props.notification.channels.includes(t.channel)
          )
        ).mergeTagsObject,
        null,
        2
      )
    );
  }, [templates, props.notification.channels]);

  return (
    <Row>
      <Col xl={12}>
        <Paragraph strong>Review Parameters (MergeTags)</Paragraph>
        <Paragraph>
          These dynamic parameters are used in your template, so your back-end
          code needs to provide them in the mergeTags field.
        </Paragraph>

        {!templates && <LoadingOutlined style={{ marginBottom: 20 }} />}

        {templates && (
          <>
            {Object.entries(
              getAllTags(
                templates.filter((t) =>
                  props.notification.channels.includes(t.channel)
                )
              ).mergeTagsList
            ).map((tag) => {
              return (
                <Paragraph code key={tag[0]}>
                  {tag[0]} ({(tag[1] as string).toLowerCase()})
                </Paragraph>
              );
            })}

            <Divider />
            <Typography.Text strong>Sample JSON:</Typography.Text>
            <div style={{ position: 'relative' }}>
              <SyntaxHighlighter
                language="json"
                style={darcula}
                customStyle={{
                  padding: 20,
                  marginTop: 6
                }}
              >
                {blockContent}
              </SyntaxHighlighter>
              <Tooltip title="Copied!" trigger="click" placement="top">
                <Button
                  style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px'
                  }}
                  icon={<CopyOutlined />}
                  onClick={() => navigator.clipboard.writeText(blockContent)}
                ></Button>
              </Tooltip>
            </div>
          </>
        )}
        <Alert
          type="info"
          message="Note"
          description={
            <>
              <Paragraph>
                If you forget to pass a parameter, the notification will still
                go out and the missing parameters will be replaced with an empty
                string.
              </Paragraph>
              <Paragraph>
                If you like to change this behavior, let us know. We are looking
                for early adopters who would be interested in a strict mode.
              </Paragraph>
            </>
          }
          showIcon={true}
        />
      </Col>
    </Row>
  );
};
export default Parameters;
